import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Breadcrumbs,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  styled,
  Container,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import {
  Check as CheckIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBackRounded as ArrowBackRoundedIcon,
  ArrowForwardIosRounded as ArrowForwardIosRoundedIcon,
  ShoppingBagOutlined as ShoppingBagOutlinedIcon,
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  PersonOutlineOutlined as PersonOutlineOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  SecurityOutlined as SecurityOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
} from "@mui/icons-material";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";
import Footer from "../Footer/Footer";
import Navbar from "../Navbar/Navbar";
import config from "../../Config";
import { PropagateLoader } from "react-spinners";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
  success: "#687C59",
  blue: "#607D8B",
  red: "#9B5A56",
};

const QontoConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: 10,
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: COLORS.border,
    borderLeftWidth: 1,
    minHeight: 45,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: COLORS.gold,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: COLORS.gold,
  },
}));

const QontoStepIconRoot = styled("div")(({ ownerState }) => ({
  color: COLORS.border,
  display: "flex",
  height: 22,
  alignItems: "center",
  justifyContent: "center",
  ...(ownerState.active && {
    color: COLORS.gold,
  }),
  "& .QontoStepIcon-completedIcon": {
    color: COLORS.gold,
    zIndex: 1,
    fontSize: 18,
  },
  "& .QontoStepIcon-circle": {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckIcon className="QontoStepIcon-completedIcon" />
      ) : (
        <Box className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

const timelineSteps = [
  {
    key: "placed",
    label: "Order Placed",
    description: "Your booking request has been created.",
    statuses: ["Pending", "Booking Confirmed", "Order Confirmed"],
  },
  {
    key: "confirmed",
    label: "Order Confirmed",
    description: "The booking has been confirmed.",
    statuses: ["Booking Confirmed", "Order Confirmed"],
  },
  {
    key: "pickup",
    label: "Order Picked-Up",
    description: "The rental item is prepared for delivery.",
    statuses: ["Order Picked-Up", "Order Shipped"],
  },
  {
    key: "received",
    label: "Item Received",
    description: "Your rental item has reached you.",
    statuses: ["Order Delivered"],
  },
  {
    key: "completed",
    label: "Order Completed",
    description: "The rental journey has been completed.",
    statuses: ["Order Completed", "Order Returned"],
  },
];

const getStatusDetails = (status) => {
  switch (status) {
    case "Pending":
      return {
        label: "Pending",
        color: COLORS.gold,
        background: "#F4EEDF",
      };
    case "Order Confirmed":
    case "Booking Confirmed":
      return {
        label: "Confirmed",
        color: COLORS.success,
        background: "#EEF2EA",
      };
    case "Order Delivered":
      return {
        label: "Delivered",
        color: COLORS.blue,
        background: "#EDF2F4",
      };
    case "Order Returned":
      return {
        label: "Returned",
        color: COLORS.red,
        background: "#F4EAEA",
      };
    case "Order Completed":
      return {
        label: "Completed",
        color: COLORS.success,
        background: "#EEF2EA",
      };
    default:
      return {
        label: status || "Unknown",
        color: COLORS.grey,
        background: COLORS.cream,
      };
  }
};

const DetailRow = ({ label, value, strong = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 2,
      py: 1.35,
      borderBottom: `1px solid ${COLORS.border}`,
      "&:last-child": {
        borderBottom: 0,
      },
    }}
  >
    <Typography
      sx={{
        fontSize: 12,
        color: COLORS.grey,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        fontSize: 13,
        color: COLORS.black,
        fontWeight: strong ? 700 : 500,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value || "N/A"}
    </Typography>
  </Box>
);

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchBookingDetails = async () => {
      if (!bookingId) {
        setError("Booking ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${config.apiBaseUrl}/${config.bookingDetails}`,
          {
            headers: {
              "booking-id": bookingId,
            },
          },
        );

        if (mounted) {
          setBookingData(response?.data?.bookingRequestData || null);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to fetch booking details.",
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchBookingDetails();

    return () => {
      mounted = false;
    };
  }, [bookingId]);

  const status = bookingData?.bookingStatus || "Unknown";

  const statusDetails = useMemo(() => getStatusDetails(status), [status]);

  const activeTimelineStep = useMemo(() => {
    if (status === "Pending") {
      return 0;
    }

    if (status === "Booking Confirmed" || status === "Order Confirmed") {
      return 1;
    }

    if (status === "Order Picked-Up" || status === "Order Shipped") {
      return 2;
    }

    if (status === "Order Delivered") {
      return 3;
    }

    if (status === "Order Completed" || status === "Order Returned") {
      return 4;
    }

    return 0;
  }, [status]);

  const rentAmount = Number(bookingData?.productData?.product?.rentAmount || 0);

  const securityAmount = Number(
    bookingData?.productData?.product?.securityAmount || 0,
  );

  const totalAmount = rentAmount;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: COLORS.cream,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Georgia, serif",
            fontSize: { xs: 30, sm: 38 },
            color: COLORS.black,
            mb: 3,
          }}
        >
          Hindustan Wardrobe
        </Typography>

        <PropagateLoader color={COLORS.gold} size={8} speedMultiplier={0.8} />
      </Box>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />

        <Box
          sx={{
            minHeight: "65vh",
            backgroundColor: COLORS.cream,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Box
            sx={{
              maxWidth: 520,
              width: "100%",
              textAlign: "center",
              py: 8,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: COLORS.gold,
                mb: 2,
              }}
            >
              Booking Details
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 34, sm: 46 },
                color: COLORS.black,
                lineHeight: 1,
                mb: 2,
              }}
            >
              Something went wrong.
            </Typography>

            <Typography
              sx={{
                color: COLORS.grey,
                fontSize: 14,
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              {error}
            </Typography>

            <Button
              onClick={() => navigate("/Booking")}
              startIcon={<ArrowBackRoundedIcon />}
              sx={{
                borderRadius: 0,
                backgroundColor: COLORS.black,
                color: COLORS.white,
                px: 4,
                py: 1.5,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.13em",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: COLORS.gold,
                },
              }}
            >
              Back to Bookings
            </Button>
          </Box>
        </Box>

        <Footer />
      </>
    );
  }

  if (!bookingData) {
    return (
      <>
        <Navbar />

        <Box
          sx={{
            minHeight: "65vh",
            backgroundColor: COLORS.cream,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: 40,
                mb: 2,
              }}
            >
              No booking found
            </Typography>

            <Button
              onClick={() => navigate("/Booking")}
              sx={{
                borderRadius: 0,
                backgroundColor: COLORS.black,
                color: COLORS.white,
                px: 4,
                py: 1.5,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              View Bookings
            </Button>
          </Box>
        </Box>

        <Footer />
      </>
    );
  }

  const product = bookingData.productData?.product || {};
  const productImage = bookingData.productData?.productImage?.[0] || "";

  const productTitle = product.title || "Fashion Rental";
  const productSize = product.size || "N/A";

  const availableDate = bookingData.productData?.slots?.availableDate || "N/A";

  const isPending = status === "Pending";

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: COLORS.cream,
          color: COLORS.black,
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            px: { xs: 2, sm: 4, md: 6 },
            py: { xs: 3, md: 5 },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                sx={{
                  fontSize: 15,
                  color: COLORS.lightGrey,
                }}
              />
            }
            sx={{
              mb: { xs: 3, md: 5 },
              "& .MuiBreadcrumbs-ol": {
                alignItems: "center",
              },
            }}
          >
            <RouterLink
              to="/Booking"
              style={{
                textDecoration: "none",
                color: COLORS.grey,
                fontSize: 12,
                letterSpacing: "0.02em",
              }}
            >
              Booking Request
            </RouterLink>

            <Typography
              sx={{
                color: COLORS.black,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Booking Details
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              mb: { xs: 4, md: 6 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "flex-end" },
              gap: 3,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: COLORS.gold,
                  mb: 1.5,
                }}
              >
                My Booking
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: { xs: 40, sm: 52, md: 64 },
                  lineHeight: 0.98,
                  letterSpacing: "-0.04em",
                  mb: 2,
                }}
              >
                Booking
                <br />
                <Box
                  component="span"
                  sx={{
                    fontStyle: "italic",
                    color: COLORS.gold,
                  }}
                >
                  details.
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: COLORS.grey,
                  fontSize: 14,
                }}
              >
                Order #{bookingData.bookingId || bookingId}
              </Typography>
            </Box>

            <Chip
              label={statusDetails.label}
              sx={{
                borderRadius: 0,
                backgroundColor: statusDetails.background,
                color: statusDetails.color,
                border: `1px solid ${statusDetails.color}40`,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                height: 34,
                px: 1,
              }}
            />
          </Box>

          <Grid container spacing={{ xs: 3, md: 5 }}>
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  borderRadius: 0,
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: COLORS.goldLight,
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => navigate(`/productdetail/${product.productId}`)}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "minmax(0, 1fr) 240px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 4 },
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 3 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ShoppingBagOutlinedIcon
                          sx={{
                            fontSize: 18,
                            color: COLORS.gold,
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.15em",
                            textTransform: "uppercase",
                            color: COLORS.grey,
                          }}
                        >
                          Rental Item
                        </Typography>
                      </Stack>

                      <ArrowForwardIosRoundedIcon
                        sx={{
                          fontSize: 14,
                          color: COLORS.gold,
                        }}
                      />
                    </Stack>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: { xs: 28, sm: 34 },
                        lineHeight: 1.1,
                        mb: 1.5,
                      }}
                    >
                      {productTitle}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 12,
                        color: COLORS.grey,
                        lineHeight: 1.7,
                        mb: 3,
                      }}
                    >
                      Click to view the complete product details.
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 1.5,
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          border: `1px solid ${COLORS.border}`,
                          p: 1.7,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 9,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: COLORS.grey,
                            mb: 0.7,
                          }}
                        >
                          Size
                        </Typography>

                        <Typography
                          sx={{
                            fontFamily: "Georgia, serif",
                            fontSize: 21,
                          }}
                        >
                          {productSize}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          border: `1px solid ${COLORS.border}`,
                          p: 1.7,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 9,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: COLORS.grey,
                            mb: 0.7,
                          }}
                        >
                          Rental
                        </Typography>

                        <Typography
                          sx={{
                            fontFamily: "Georgia, serif",
                            fontSize: 21,
                          }}
                        >
                          ₹{rentAmount}
                          <Box
                            component="span"
                            sx={{
                              fontFamily:
                                '"Inter", "Helvetica Neue", Arial, sans-serif',
                              fontSize: 10,
                              color: COLORS.grey,
                              ml: 0.5,
                            }}
                          >
                            /day
                          </Box>
                        </Typography>
                      </Box>
                    </Box>

                    {isPending ? (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          mt: "auto",
                          pt: 1,
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            flex: 1,
                            borderRadius: 0,
                            backgroundColor: COLORS.black,
                            color: COLORS.white,
                            py: 1.3,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            "&:hover": {
                              backgroundColor: COLORS.gold,
                            },
                          }}
                        >
                          Confirm
                        </Button>

                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            flex: 1,
                            borderRadius: 0,
                            color: COLORS.mutedBlack,
                            borderColor: COLORS.border,
                            py: 1.3,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            "&:hover": {
                              borderColor: COLORS.black,
                              backgroundColor: COLORS.cream,
                            },
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          mt: "auto",
                          pt: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            py: 1.4,
                            textAlign: "center",
                            backgroundColor: statusDetails.background,
                            color: statusDetails.color,
                            border: `1px solid ${statusDetails.color}30`,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                          }}
                        >
                          {statusDetails.label}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      minHeight: { xs: 300, sm: 390 },
                      backgroundColor: COLORS.cream,
                      overflow: "hidden",
                    }}
                  >
                    {productImage ? (
                      <Box
                        component="img"
                        src={productImage}
                        alt={productTitle}
                        sx={{
                          width: "100%",
                          height: "100%",
                          minHeight: { xs: 300, sm: 390 },
                          objectFit: "cover",
                          display: "block",
                          transition: "transform 0.5s ease",
                          "&:hover": {
                            transform: "scale(1.03)",
                          },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          minHeight: { xs: 300, sm: 390 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: COLORS.lightGrey,
                        }}
                      >
                        <ShoppingBagOutlinedIcon sx={{ fontSize: 45 }} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  borderRadius: 0,
                  p: { xs: 2.5, sm: 4 },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ mb: 3 }}
                >
                  <ReceiptLongOutlinedIcon
                    sx={{
                      color: COLORS.gold,
                      fontSize: 20,
                    }}
                  />

                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 25,
                    }}
                  >
                    Billing Details
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                    pb: 2,
                    borderBottom: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 9,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: COLORS.grey,
                        mb: 0.6,
                      }}
                    >
                      Booking ID
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {bookingData.bookingId || bookingId}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                    <Typography
                      sx={{
                        fontSize: 9,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: COLORS.grey,
                        mb: 0.6,
                      }}
                    >
                      Booking Date
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {bookingData.bookingDate || "N/A"}
                    </Typography>
                  </Box>
                </Box>

                <DetailRow label="Product" value={productTitle} />

                <DetailRow label="Rental Price" value={`₹${rentAmount}/day`} />

                <DetailRow
                  label="Security Deposit"
                  value={`₹${securityAmount}`}
                />

                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${COLORS.black}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    Total
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 26,
                    }}
                  >
                    ₹{totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Paper>

              <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      borderRadius: 0,
                      p: { xs: 2.5, sm: 3 },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 3 }}
                    >
                      <PersonOutlineOutlinedIcon
                        sx={{
                          color: COLORS.gold,
                          fontSize: 20,
                        }}
                      />

                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 23,
                        }}
                      >
                        Customer
                      </Typography>
                    </Stack>

                    <Stack spacing={1.8}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 9,
                            color: COLORS.grey,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            mb: 0.5,
                          }}
                        >
                          Name
                        </Typography>

                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {bookingData.userData?.name || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 9,
                            color: COLORS.grey,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            mb: 0.5,
                          }}
                        >
                          Email
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {bookingData.userData?.email || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 9,
                            color: COLORS.grey,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            mb: 0.5,
                          }}
                        >
                          Phone
                        </Typography>

                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {bookingData.userData?.contact || "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      borderRadius: 0,
                      p: { xs: 2.5, sm: 3 },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 3 }}
                    >
                      <LocationOnOutlinedIcon
                        sx={{
                          color: COLORS.gold,
                          fontSize: 20,
                        }}
                      />

                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 23,
                        }}
                      >
                        Delivery Address
                      </Typography>
                    </Stack>

                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      {bookingData.deliveryAddress?.name ||
                        bookingData.userData?.name ||
                        "N/A"}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: COLORS.grey,
                        lineHeight: 1.8,
                      }}
                    >
                      {bookingData.deliveryAddress?.street1 || ""}
                      {bookingData.deliveryAddress?.street2
                        ? `, ${bookingData.deliveryAddress.street2}`
                        : ""}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: COLORS.grey,
                        lineHeight: 1.8,
                      }}
                    >
                      {bookingData.deliveryAddress?.city || "N/A"}
                      {bookingData.deliveryAddress?.state
                        ? `, ${bookingData.deliveryAddress.state}`
                        : ""}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: COLORS.grey,
                        lineHeight: 1.8,
                      }}
                    >
                      {bookingData.deliveryAddress?.country || "India"}
                      {bookingData.deliveryAddress?.pinCode
                        ? ` - ${bookingData.deliveryAddress.pinCode}`
                        : ""}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  borderRadius: 0,
                  p: { xs: 2.5, sm: 4 },
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ mb: 4 }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.17em",
                        textTransform: "uppercase",
                        color: COLORS.gold,
                        mb: 1,
                      }}
                    >
                      Your Journey
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 28,
                      }}
                    >
                      Order Timeline
                    </Typography>
                  </Box>

                  <LocalShippingOutlinedIcon
                    sx={{
                      color: COLORS.gold,
                      fontSize: 25,
                    }}
                  />
                </Stack>

                <Stepper
                  activeStep={activeTimelineStep}
                  orientation="vertical"
                  connector={<QontoConnector />}
                  sx={{
                    "& .MuiStepLabel-labelContainer": {
                      width: "100%",
                    },
                    "& .MuiStepLabel-root": {
                      alignItems: "flex-start",
                    },
                    "& .MuiStep-root": {
                      pb: 2,
                    },
                  }}
                >
                  {timelineSteps.map((step, index) => {
                    const completed = index < activeTimelineStep;
                    const active = index === activeTimelineStep;

                    return (
                      <Step
                        key={step.key}
                        completed={completed}
                        active={active}
                      >
                        <StepLabel StepIconComponent={QontoStepIcon}>
                          <Box sx={{ pb: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: active || completed ? 700 : 500,
                                color:
                                  active || completed
                                    ? COLORS.black
                                    : COLORS.lightGrey,
                                mb: 0.5,
                              }}
                            >
                              {step.label}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 11,
                                lineHeight: 1.6,
                                color:
                                  active || completed
                                    ? COLORS.grey
                                    : COLORS.lightGrey,
                              }}
                            >
                              {step.description}
                            </Typography>

                            {step.key === "received" &&
                              availableDate !== "N/A" && (
                                <Typography
                                  sx={{
                                    mt: 1,
                                    fontSize: 10,
                                    color: COLORS.gold,
                                    fontWeight: 700,
                                    letterSpacing: "0.04em",
                                  }}
                                >
                                  Expected: {availableDate}
                                </Typography>
                              )}
                          </Box>
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.black,
                  color: COLORS.white,
                  borderRadius: 0,
                  p: { xs: 2.5, sm: 3.5 },
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: 160,
                    height: 160,
                    border: `1px solid rgba(196,163,106,0.3)`,
                    borderRadius: "50%",
                    right: -70,
                    top: -75,
                  }}
                />

                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <SecurityOutlinedIcon
                    sx={{
                      color: COLORS.goldLight,
                      fontSize: 24,
                      mt: 0.3,
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 22,
                        mb: 1,
                      }}
                    >
                      Rental Protection
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 12,
                        lineHeight: 1.8,
                        color: "#BEBAB3",
                        maxWidth: 400,
                      }}
                    >
                      Your booking information and rental details are securely
                      associated with this order.
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  onClick={() => navigate("/Booking")}
                  startIcon={<ArrowBackRoundedIcon />}
                  sx={{
                    width: "100%",
                    borderRadius: 0,
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.black,
                    backgroundColor: COLORS.white,
                    py: 1.5,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                    "&:hover": {
                      backgroundColor: COLORS.black,
                      color: COLORS.white,
                      borderColor: COLORS.black,
                    },
                  }}
                >
                  Back to Booking Requests
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
    </>
  );
};

export default BookingDetails;
